'use client';

import { useEffect, type RefObject } from 'react';

/**
 * Magnetic micro-physics for dock buttons (MOTION-SPEC §9, README motion rules).
 *
 * One passive `pointermove` on the dock root finds the `[data-magnet]` button
 * under the pointer and writes `--sn-mag-x/--sn-mag-y` (≤ ±max px, lerped) on
 * that element inside requestAnimationFrame. No React state, no CSS transition
 * on the driven transform; the loop self-stops when settled and clears the
 * variables when the pointer leaves. Callers pass `enabled = false` on touch
 * and under reduced motion so the effect never attaches there.
 */
export function useMagnet(ref: RefObject<HTMLElement | null>, enabled: boolean, max = 3): void {
  useEffect(() => {
    const root = ref.current;
    if (!enabled || !root) return;

    let raf = 0;
    let active: HTMLElement | null = null;
    let rect: DOMRect | null = null;
    const cur = { x: 0, y: 0 };
    const tgt = { x: 0, y: 0 };

    const clear = (element: HTMLElement) => {
      element.style.removeProperty('--sn-mag-x');
      element.style.removeProperty('--sn-mag-y');
    };

    const tick = () => {
      raf = 0;
      cur.x += (tgt.x - cur.x) * 0.28;
      cur.y += (tgt.y - cur.y) * 0.28;
      const settled = Math.abs(tgt.x - cur.x) < 0.02 && Math.abs(tgt.y - cur.y) < 0.02;
      if (settled) {
        cur.x = tgt.x;
        cur.y = tgt.y;
      }
      if (active) {
        if (settled && tgt.x === 0 && tgt.y === 0) {
          clear(active);
          active = null;
          rect = null;
        } else {
          active.style.setProperty('--sn-mag-x', `${cur.x.toFixed(2)}px`);
          active.style.setProperty('--sn-mag-y', `${cur.y.toFixed(2)}px`);
        }
      }
      if (!settled) raf = requestAnimationFrame(tick);
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const release = () => {
      tgt.x = 0;
      tgt.y = 0;
      schedule();
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-magnet]') : null;
      if (!target) {
        release();
        return;
      }
      if (target !== active) {
        if (active) clear(active);
        active = target;
        rect = target.getBoundingClientRect();
        cur.x = 0;
        cur.y = 0;
      }
      if (!rect) return;
      const nx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const ny = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      tgt.x = Math.max(-1, Math.min(1, nx)) * max;
      tgt.y = Math.max(-1, Math.min(1, ny)) * max;
      schedule();
    };

    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', release);
    return () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', release);
      if (raf) cancelAnimationFrame(raf);
      if (active) clear(active);
    };
  }, [ref, enabled, max]);
}
